// src/pages/aspirant/AspirantReports.tsx
import React, { useState, useEffect } from 'react';
import { 
  Filter, 
  Download, 
  Search, 
  Calendar,
  MapPin,
  AlertTriangle,
  CheckCircle,
  Clock,
  Eye,
  MessageSquare,
  RefreshCw,
  Users,
  TrendingUp,
  ArrowLeft,
  BarChart3,
  FileText,
  User,
  Phone,
  X,
  ChevronRight,
  FileBarChart,
  ShieldAlert,
  Wrench,
  Image,
  File,
  Paperclip,
  Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiService, API_BASE_URL } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

interface Attachment {
  index: number;
  url: string;
  filename: string;
  contentType: string;
}

interface Report {
  id: number;
  reportId: string;
  description: string;
  category: string;
  categoryDisplayName: string;
  status: string;
  urgencyScore: number;
  phoneNumber: string;
  fullName?: string;
  wardId?: number;
  wardName?: string;
  constituencyId?: number;
  constituencyName?: string;
  location?: string;
  reportedAt: string;
  isAnonymous?: boolean;
  notes?: string;
  resolutionDetails?: string;
  attachments?: string[]; // URLs from the database
}

interface Aspirant {
  id: number;
  name?: string;
  wardId?: number;
  wardName?: string;
  constituencyId?: number;
  constituencyName?: string;
  [key: string]: any;
}

const AspirantReports: React.FC = () => {
  const navigate = useNavigate();
  const { aspirant } = useAuth();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    search: '',
    dateRange: 'all',
    urgencyMin: 0,
  });

  const [showFilters, setShowFilters] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  
  // Attachment modal state
  const [selectedAttachment, setSelectedAttachment] = useState<{
    url: string;
    filename: string;
    contentType: string;
  } | null>(null);
  const [attachmentLoading, setAttachmentLoading] = useState(false);
  const [attachmentData, setAttachmentData] = useState<{
    [key: string]: Attachment[];
  }>({});

  const typedAspirant = aspirant as Aspirant | null;

  // Helper function to mask phone number (show first 3 digits, hide last 3-4 digits)
  const maskPhoneNumber = (phoneNumber: string): string => {
    if (!phoneNumber) return 'Not provided';
    
    // Remove any whitespace
    const cleaned = phoneNumber.trim();
    
    // If phone number is too short, just return asterisks
    if (cleaned.length <= 4) {
      return '*'.repeat(cleaned.length);
    }
    
    // Show first 3 digits, then asterisks for the rest, but keep the last digit visible for reference
    // Example: 254748955812 -> 254******812
    const visibleStart = cleaned.substring(0, 3);
    const visibleEnd = cleaned.substring(cleaned.length - 3);
    const maskedLength = cleaned.length - 6;
    const asterisks = '*'.repeat(maskedLength);
    
    return `${visibleStart}${asterisks}${visibleEnd}`;
  };

  useEffect(() => {
    if (typedAspirant?.id) {
      fetchAspirantReports();
    } else {
      setError('Aspirant information not found. Please login again.');
      setLoading(false);
    }
  }, [typedAspirant?.id]);

  const fetchAspirantReports = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.getAspirantReports(typedAspirant!.id);
      
      if (response.data.success) {
        const reportsData = response.data.data || [];
        setReports(reportsData);
        
        // Fetch attachments for each report that has them
        for (const report of reportsData) {
          if (report.attachments && report.attachments.length > 0) {
            await fetchAttachmentsInfo(report.reportId);
          }
        }
        
        if (reportsData.length === 0) {
          toast.success('No reports found for your ward');
        } else {
          toast.success(`Loaded ${reportsData.length} reports from your ward`);
        }
      } else {
        setReports([]);
        toast.error(response.data.message || 'No reports found for your ward');
      }
    } catch (error: any) {
      console.error('Error fetching reports:', error);
      setError('Failed to load reports. Please try again.');
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttachmentsInfo = async (reportId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/v1/citizen-reports/${reportId}/attachments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setAttachmentData(prev => ({
            ...prev,
            [reportId]: data.data
          }));
        }
      }
    } catch (error) {
      console.error(`Error fetching attachments for report ${reportId}:`, error);
    }
  };

  const viewAttachment = async (reportId: string, attachment: Attachment) => {
    setAttachmentLoading(true);
    setSelectedAttachment({
      url: attachment.url,
      filename: attachment.filename,
      contentType: attachment.contentType
    });
    
    // Pre-load the attachment data
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/v1/citizen-reports/${reportId}/attachment/${attachment.index}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setSelectedAttachment(prev => prev ? { ...prev, url: url } : null);
      }
    } catch (error) {
      console.error('Error loading attachment:', error);
      toast.error('Failed to load attachment');
    } finally {
      setAttachmentLoading(false);
    }
  };

  const downloadAttachment = async (reportId: string, attachment: Attachment) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/v1/citizen-reports/${reportId}/attachment/${attachment.index}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download');
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast.error('Failed to download attachment');
    }
  };

  const closeAttachmentModal = () => {
    if (selectedAttachment?.url && selectedAttachment.url.startsWith('blob:')) {
      URL.revokeObjectURL(selectedAttachment.url);
    }
    setSelectedAttachment(null);
  };

  const handleExport = () => {
    try {
      const csvContent = convertToCSV(filteredReports);
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ward-reports-${typedAspirant?.wardName || 'ward'}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Reports exported successfully');
    } catch (error) {
      toast.error('Failed to export reports');
    }
  };

  const handleViewDetails = (report: Report) => {
    setSelectedReport(report);
  };

  const handleUpdateStatus = async (reportId: string, newStatus: string) => {
    try {
      setUpdatingStatus(reportId);
      const response = await apiService.updateReportStatus(reportId, { 
        status: newStatus,
        notes: `Status updated by aspirant ${typedAspirant?.name || typedAspirant?.id}`
      });
      
      if (response.data.success) {
        toast.success(`Report marked as ${newStatus.toLowerCase().replace('_', ' ')}`);
        
        // Update local state
        setReports(prevReports => 
          prevReports.map(report => 
            report.reportId === reportId 
              ? { ...report, status: newStatus }
              : report
          )
        );
        
        if (selectedReport?.reportId === reportId) {
          setSelectedReport(prev => prev ? { ...prev, status: newStatus } : null);
        }
      } else {
        toast.error(response.data.message || 'Failed to update status');
      }
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleAddComment = (reportId: string) => {
    const comment = prompt('Enter your comment:');
    if (comment) {
      toast.success('Comment added successfully');
    }
  };

  const convertToCSV = (reports: Report[]): string => {
    const headers = ['Report ID', 'Description', 'Category', 'Status', 'Urgency', 'Ward', 'Constituency', 'Date', 'Location', 'Citizen Name', 'Phone', 'Has Attachments'];
    const rows = reports.map(report => [
      report.reportId,
      `"${(report.description || '').replace(/"/g, '""')}"`,
      report.categoryDisplayName || report.category,
      report.status,
      report.urgencyScore.toString(),
      report.wardName || 'N/A',
      report.constituencyName || 'N/A',
      new Date(report.reportedAt).toLocaleDateString(),
      report.location || 'N/A',
      report.isAnonymous ? 'Anonymous' : report.fullName || 'N/A',
      maskPhoneNumber(report.phoneNumber || 'N/A'), // Mask phone number in CSV export
      (report.attachments && report.attachments.length > 0) ? 'Yes' : 'No'
    ]);
    
    return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  };

  const getStatusCount = (status: string) => {
    return reports.filter(r => r.status === status).length;
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'RESOLVED': return <CheckCircle className="text-[#3c8d2e]" size={16} />;
      case 'IN_PROGRESS': return <Wrench className="text-[#0b2045]" size={16} />;
      case 'PENDING': return <Clock className="text-[#ca8a04]" size={16} />;
      default: return <Clock className="text-gray-500" size={16} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'RESOLVED': return 'bg-[#3c8d2e]/10 text-[#3c8d2e]';
      case 'IN_PROGRESS': return 'bg-[#0b2045]/10 text-[#0b2045]';
      case 'PENDING': return 'bg-[#fefce8] text-[#ca8a04]';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getUrgencyColor = (urgency: number) => {
    if (urgency >= 9) return 'bg-red-500';
    if (urgency >= 7) return 'bg-[#ca8a04]';
    if (urgency >= 5) return 'bg-[#fefce8]';
    return 'bg-[#3c8d2e]';
  };

  const getUrgencyText = (urgency: number) => {
    if (urgency >= 9) return 'Critical';
    if (urgency >= 7) return 'High';
    if (urgency >= 5) return 'Medium';
    return 'Low';
  };

  const getFileIcon = (contentType: string) => {
    if (contentType?.startsWith('image/')) return <Image size={20} className="text-[#0b2045]" />;
    if (contentType === 'application/pdf') return <File size={20} className="text-red-500" />;
    return <File size={20} className="text-gray-500" />;
  };

  // Filter reports
  const filteredReports = reports.filter(report => {
    if (filters.status !== 'all' && report.status !== filters.status) return false;
    if (filters.category !== 'all' && report.category !== filters.category) return false;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matches = 
        (report.description || '').toLowerCase().includes(searchLower) ||
        (report.reportId || '').toLowerCase().includes(searchLower) ||
        (report.categoryDisplayName || report.category || '').toLowerCase().includes(searchLower) ||
        (report.location || '').toLowerCase().includes(searchLower) ||
        (report.fullName || '').toLowerCase().includes(searchLower);
      if (!matches) return false;
    }
    if (filters.urgencyMin > 0 && report.urgencyScore < filters.urgencyMin) return false;
    if (filters.dateRange !== 'all') {
      const reportDate = new Date(report.reportedAt);
      const now = new Date();
      const daysDiff = Math.floor((now.getTime() - reportDate.getTime()) / (1000 * 60 * 60 * 24));
      if (filters.dateRange === 'today' && daysDiff > 0) return false;
      if (filters.dateRange === 'week' && daysDiff > 7) return false;
      if (filters.dateRange === 'month' && daysDiff > 30) return false;
    }
    return true;
  });

  const totalReports = reports.length;
  const pendingReports = getStatusCount('PENDING');
  const inProgressReports = getStatusCount('IN_PROGRESS');
  const resolvedReports = getStatusCount('RESOLVED');
  const urgentReports = reports.filter(r => r.urgencyScore >= 7).length;
  const resolutionRate = totalReports > 0 ? Math.round((resolvedReports / totalReports) * 100) : 0;

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'WATER', label: 'Water & Sanitation' },
    { value: 'ROADS', label: 'Roads & Transport' },
    { value: 'HEALTH', label: 'Healthcare' },
    { value: 'EDUCATION', label: 'Education' },
    { value: 'SECURITY', label: 'Security' },
    { value: 'SANITATION', label: 'Waste Management' },
    { value: 'ELECTRICITY', label: 'Electricity' },
    { value: 'OTHER', label: 'Other' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto mb-4 text-[#0b2045]" size={40} />
          <p className="text-gray-600">Loading ward reports...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <AlertTriangle className="mx-auto text-red-500 mb-4" size={48} />
        <h3 className="text-lg font-medium text-[#0b2045] mb-2">Error Loading Reports</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={fetchAspirantReports}
          className="px-6 py-3 bg-[#0b2045] text-white rounded-lg hover:bg-[#152c53] font-medium transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center text-gray-600 text-sm mb-2">
            <button
              onClick={() => navigate('/aspirant/dashboard')}
              className="flex items-center hover:text-[#0b2045] transition-colors"
            >
              <ArrowLeft size={16} className="mr-1" />
              Dashboard
            </button>
            <ChevronRight size={16} className="mx-2" />
            <span className="font-medium text-[#0b2045]">Ward Reports</span>
          </div>
          <h1 className="text-2xl font-bold text-[#0b2045]">Citizen Reports</h1>
          <p className="text-gray-600">Manage and respond to reports from citizens in your ward</p>
          {typedAspirant?.wardName && (
            <div className="flex items-center gap-2 mt-2">
              <MapPin size={16} className="text-gray-400" />
              <span className="text-sm font-medium text-[#0b2045]">Ward: {typedAspirant.wardName}</span>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center px-4 py-2 border rounded-lg text-sm font-medium ${
              showFilters 
                ? 'bg-[#0b2045]/10 border-[#0b2045] text-[#0b2045]' 
                : 'border-gray-300 hover:bg-[#f4f7f6]'
            }`}
          >
            <Filter size={16} className="mr-2" />
            Filters
          </button>
          <button
            onClick={handleExport}
            disabled={filteredReports.length === 0}
            className="inline-flex items-center px-4 py-2 bg-[#0b2045] text-white rounded-lg text-sm font-medium hover:bg-[#152c53] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Download size={16} className="mr-2" />
            Export ({filteredReports.length})
          </button>
          <button
            onClick={fetchAspirantReports}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-[#f4f7f6] transition-colors"
          >
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-[#0b2045]">
          <div className="flex items-center">
            <div className="p-2 bg-[#0b2045]/10 rounded-lg mr-3">
              <FileBarChart className="text-[#0b2045]" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Reports</p>
              <p className="text-xl font-bold text-[#0b2045]">{totalReports}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-[#ca8a04]">
          <div className="flex items-center">
            <div className="p-2 bg-[#fefce8] rounded-lg mr-3">
              <Clock className="text-[#ca8a04]" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-xl font-bold text-[#0b2045]">{pendingReports}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-[#3c8d2e]">
          <div className="flex items-center">
            <div className="p-2 bg-[#3c8d2e]/10 rounded-lg mr-3">
              <CheckCircle className="text-[#3c8d2e]" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Resolved</p>
              <p className="text-xl font-bold text-[#0b2045]">{resolvedReports}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-[#0b2045]">
          <div className="flex items-center">
            <div className="p-2 bg-[#0b2045]/10 rounded-lg mr-3">
              <TrendingUp className="text-[#0b2045]" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Resolution Rate</p>
              <p className="text-xl font-bold text-[#0b2045]">{resolutionRate}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-[#0b2045]/10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="all">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="RESOLVED">Resolved</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              >
                {categoryOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
                value={filters.dateRange}
                onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Urgency ({filters.urgencyMin}+)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={filters.urgencyMin}
                  onChange={(e) => setFilters({ ...filters, urgencyMin: parseInt(e.target.value) })}
                  className="flex-1 accent-[#3c8d2e]"
                />
                <span className="text-sm font-medium text-[#0b2045] w-8">
                  {filters.urgencyMin}
                </span>
              </div>
            </div>
          </div>
          
          <div className="mt-6 pt-6 border-t border-gray-200 flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Showing {filteredReports.length} of {reports.length} reports
            </div>
            <button
              onClick={() => setFilters({
                status: 'all',
                category: 'all',
                search: '',
                dateRange: 'all',
                urgencyMin: 0,
              })}
              className="px-4 py-2 text-sm text-[#3c8d2e] hover:text-[#2d7022] font-medium hover:bg-[#e8f1ec] rounded-lg transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-[#0b2045]/10">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="search"
            placeholder="Search reports by description, category, location, or citizen name..."
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-[#0b2045]/10">
        {filteredReports.length === 0 ? (
          <div className="p-12 text-center">
            <BarChart3 className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-medium text-[#0b2045] mb-2">
              {reports.length === 0 
                ? 'No reports in your ward yet' 
                : 'No reports match your filters'}
            </h3>
            <p className="text-gray-600 mb-4">
              {reports.length === 0
                ? 'Citizens in your ward haven\'t submitted any reports yet.'
                : 'Try adjusting your filters to see more reports.'}
            </p>
          </div>
        ) : (
          <>
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-[#0b2045]">
                  Reports ({filteredReports.length})
                </h3>
                <div className="text-sm text-gray-500">
                  Sorted by: <span className="font-medium text-[#0b2045]">Most Recent</span>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-[#f4f7f6]">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                      Report Details
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                      Citizen
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                      Category
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                      Urgency
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredReports.map((report) => {
                    const reportAttachments = attachmentData[report.reportId] || [];
                    const hasAttachments = reportAttachments.length > 0;
                    
                    return (
                      <tr key={report.id} className="hover:bg-[#f4f7f6] transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 mt-1">
                              {getStatusIcon(report.status)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-medium text-[#0b2045]">
                                  {report.reportId}
                                </span>
                                {report.urgencyScore >= 7 && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                    <ShieldAlert size={10} className="mr-1" />
                                    Urgent
                                  </span>
                                )}
                                {hasAttachments && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#f4f7f6] text-[#0b2045]">
                                    <Paperclip size={10} className="mr-1" />
                                    {reportAttachments.length}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-[#0b2045] mb-2 line-clamp-2">{report.description}</p>
                              
                              {/* Attachment Thumbnails */}
                              {hasAttachments && (
                                <div className="flex gap-2 mt-2">
                                  {reportAttachments.slice(0, 3).map((attachment, idx) => (
                                    <button
                                      key={idx}
                                      onClick={() => viewAttachment(report.reportId, attachment)}
                                      className="w-12 h-12 bg-[#f4f7f6] rounded-lg overflow-hidden hover:ring-2 hover:ring-[#3c8d2e] transition-all flex items-center justify-center"
                                    >
                                      {getFileIcon(attachment.contentType)}
                                    </button>
                                  ))}
                                  {reportAttachments.length > 3 && (
                                    <button
                                      onClick={() => {
                                        // Show all attachments in modal
                                        setSelectedReport(report);
                                      }}
                                      className="w-12 h-12 bg-[#f4f7f6] rounded-lg flex items-center justify-center text-xs font-medium text-[#0b2045] hover:bg-[#e8f1ec]"
                                    >
                                      +{reportAttachments.length - 3}
                                    </button>
                                  )}
                                </div>
                              )}
                              
                              <div className="flex items-center text-xs text-gray-500 mt-2">
                                <Calendar size={12} className="mr-1" />
                                {new Date(report.reportedAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                                {report.location && (
                                  <>
                                    <span className="mx-2">•</span>
                                    <MapPin size={12} className="mr-1" />
                                    {report.location}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <div className="text-sm font-medium text-[#0b2045]">
                              {report.isAnonymous ? 'Anonymous' : report.fullName || 'Not Provided'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {maskPhoneNumber(report.phoneNumber)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0b2045]/10 text-[#0b2045]">
                            {report.categoryDisplayName || report.category}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                              {getStatusIcon(report.status)}
                              <span className="ml-1.5">
                                {report.status?.replace('_', ' ') || 'Unknown'}
                              </span>
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-24 mr-3">
                              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                                <span>{getUrgencyText(report.urgencyScore)}</span>
                                <span>{report.urgencyScore}/10</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${getUrgencyColor(report.urgencyScore)}`}
                                  style={{ width: `${report.urgencyScore * 10}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewDetails(report)}
                              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-[#0b2045] bg-[#0b2045]/10 hover:bg-[#0b2045]/20 rounded-lg transition-colors"
                            >
                              <Eye size={14} className="mr-1.5" />
                              View
                            </button>
                            
                            {report.status === 'PENDING' && (
                              <button
                                onClick={() => handleUpdateStatus(report.reportId, 'IN_PROGRESS')}
                                disabled={updatingStatus === report.reportId}
                                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-[#3c8d2e] bg-[#3c8d2e]/10 hover:bg-[#3c8d2e]/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {updatingStatus === report.reportId ? (
                                  <RefreshCw size={14} className="mr-1.5 animate-spin" />
                                ) : (
                                  <Wrench size={14} className="mr-1.5" />
                                )}
                                Start
                              </button>
                            )}
                            
                            {report.status === 'IN_PROGRESS' && (
                              <button
                                onClick={() => handleUpdateStatus(report.reportId, 'RESOLVED')}
                                disabled={updatingStatus === report.reportId}
                                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-[#0b2045] bg-[#0b2045]/10 hover:bg-[#0b2045]/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {updatingStatus === report.reportId ? (
                                  <RefreshCw size={14} className="mr-1.5 animate-spin" />
                                ) : (
                                  <CheckCircle size={14} className="mr-1.5" />
                                )}
                                Resolve
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Report Details Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-[#0b2045]/10">
            <div className="sticky top-0 bg-white border-b border-[#0b2045]/10 z-10">
              <div className="p-6 flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-[#0b2045]">Report Details</h2>
                  <p className="text-gray-600">{selectedReport.reportId}</p>
                </div>
                <button
                  onClick={() => setSelectedReport(null)}
                  className="p-2 text-gray-400 hover:text-[#0b2045] hover:bg-[#f4f7f6] rounded-lg transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Status and Urgency Bar */}
              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-4">
                  <div className={`inline-flex items-center px-4 py-2 rounded-lg ${getStatusColor(selectedReport.status)}`}>
                    {getStatusIcon(selectedReport.status)}
                    <span className="ml-2 font-medium capitalize">
                      {selectedReport.status?.toLowerCase().replace('_', ' ')}
                    </span>
                  </div>
                  
                  <div className={`inline-flex items-center px-4 py-2 rounded-lg ${
                    selectedReport.urgencyScore >= 7 
                      ? 'bg-red-100 text-red-800' 
                      : selectedReport.urgencyScore >= 5
                      ? 'bg-[#fefce8] text-[#ca8a04]'
                      : 'bg-[#3c8d2e]/10 text-[#3c8d2e]'
                  }`}>
                    <AlertTriangle size={16} className="mr-2" />
                    <span className="font-medium">
                      {getUrgencyText(selectedReport.urgencyScore)} Urgency ({selectedReport.urgencyScore}/10)
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Issue Details */}
                  <div className="bg-white border border-[#0b2045]/10 rounded-lg p-5">
                    <h3 className="text-lg font-semibold text-[#0b2045] mb-4 flex items-center">
                      <FileText className="mr-2 text-[#0b2045]" size={20} />
                      Issue Details
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Category</label>
                        <p className="font-medium text-lg text-[#0b2045]">
                          {selectedReport.categoryDisplayName || selectedReport.category}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Description</label>
                        <div className="p-3 bg-[#f4f7f6] rounded-lg border border-[#0b2045]/10">
                          <p className="text-[#0b2045] whitespace-pre-line">{selectedReport.description}</p>
                        </div>
                      </div>
                      
                      {/* Attachments Section */}
                      {attachmentData[selectedReport.reportId] && attachmentData[selectedReport.reportId].length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-2">Attachments</label>
                          <div className="grid grid-cols-4 gap-3">
                            {attachmentData[selectedReport.reportId].map((attachment, idx) => (
                              <div key={idx} className="relative group">
                                <button
                                  onClick={() => viewAttachment(selectedReport.reportId, attachment)}
                                  className="w-full aspect-square bg-[#f4f7f6] rounded-lg overflow-hidden hover:ring-2 hover:ring-[#3c8d2e] transition-all flex flex-col items-center justify-center p-2"
                                >
                                  {getFileIcon(attachment.contentType)}
                                  <span className="text-xs text-[#0b2045] mt-1 truncate w-full text-center">
                                    {attachment.filename.length > 20 
                                      ? attachment.filename.substring(0, 17) + '...' 
                                      : attachment.filename}
                                  </span>
                                </button>
                                <button
                                  onClick={() => downloadAttachment(selectedReport.reportId, attachment)}
                                  className="absolute top-1 right-1 p-1 bg-white rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <Download size={12} className="text-[#0b2045]" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {selectedReport.location && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Location</label>
                          <div className="flex items-center text-[#0b2045]">
                            <MapPin size={16} className="mr-2 text-gray-400" />
                            {selectedReport.location}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Administrative Area */}
                  <div className="bg-white border border-[#0b2045]/10 rounded-lg p-5">
                    <h3 className="text-lg font-semibold text-[#0b2045] mb-4 flex items-center">
                      <MapPin className="mr-2 text-[#3c8d2e]" size={20} />
                      Administrative Area
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Ward</label>
                        <p className="font-medium text-[#0b2045]">{selectedReport.wardName || 'Not specified'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Constituency</label>
                        <p className="font-medium text-[#0b2045]">{selectedReport.constituencyName || 'Not specified'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Citizen Information */}
                  <div className="bg-white border border-[#0b2045]/10 rounded-lg p-5">
                    <h3 className="text-lg font-semibold text-[#0b2045] mb-4 flex items-center">
                      <User className="mr-2 text-[#0b2045]" size={20} />
                      {selectedReport.isAnonymous ? 'Anonymous Report' : 'Citizen Information'}
                    </h3>
                    {!selectedReport.isAnonymous ? (
                      <div className="space-y-4">
                        <div className="flex items-center">
                          <User className="text-gray-400 mr-3" size={18} />
                          <div>
                            <p className="text-sm text-gray-500">Full Name</p>
                            <p className="font-medium text-[#0b2045]">{selectedReport.fullName || 'Not provided'}</p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <Phone className="text-gray-400 mr-3" size={18} />
                          <div>
                            <p className="text-sm text-gray-500">Phone Number</p>
                            <p className="font-medium text-[#0b2045]">{maskPhoneNumber(selectedReport.phoneNumber || 'Not provided')}</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-[#0b2045] italic">This report was submitted anonymously</p>
                    )}
                  </div>

                  {/* Report Timeline */}
                  <div className="bg-white border border-[#0b2045]/10 rounded-lg p-5">
                    <h3 className="text-lg font-semibold text-[#0b2045] mb-4 flex items-center">
                      <Calendar className="mr-2 text-[#ca8a04]" size={20} />
                      Report Timeline
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <div className="w-3 h-3 rounded-full bg-[#0b2045] mr-3"></div>
                          <span className="text-sm">Reported</span>
                        </div>
                        <span className="font-medium text-sm text-[#0b2045]">
                          {new Date(selectedReport.reportedAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="bg-white border border-[#0b2045]/10 rounded-lg p-5">
                    <h3 className="text-lg font-semibold text-[#0b2045] mb-4">Quick Actions</h3>
                    <div className="space-y-3">
                      {selectedReport.status === 'PENDING' && (
                        <button
                          onClick={() => {
                            handleUpdateStatus(selectedReport.reportId, 'IN_PROGRESS');
                            setSelectedReport(null);
                          }}
                          disabled={updatingStatus === selectedReport.reportId}
                          className="w-full px-4 py-3 bg-[#0b2045] text-white rounded-lg hover:bg-[#152c53] font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                        >
                          {updatingStatus === selectedReport.reportId ? (
                            <RefreshCw className="animate-spin mr-2" size={18} />
                          ) : (
                            <Wrench className="mr-2" size={18} />
                          )}
                          Start Working on This Issue
                        </button>
                      )}
                      
                      {selectedReport.status === 'IN_PROGRESS' && (
                        <button
                          onClick={() => {
                            handleUpdateStatus(selectedReport.reportId, 'RESOLVED');
                            setSelectedReport(null);
                          }}
                          disabled={updatingStatus === selectedReport.reportId}
                          className="w-full px-4 py-3 bg-[#3c8d2e] text-white rounded-lg hover:bg-[#2d7022] font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                        >
                          {updatingStatus === selectedReport.reportId ? (
                            <RefreshCw className="animate-spin mr-2" size={18} />
                          ) : (
                            <CheckCircle className="mr-2" size={18} />
                          )}
                          Mark as Resolved
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleAddComment(selectedReport.reportId)}
                        className="w-full px-4 py-3 border border-gray-300 text-[#0b2045] rounded-lg hover:bg-[#f4f7f6] font-medium flex items-center justify-center transition-colors"
                      >
                        <MessageSquare className="mr-2" size={18} />
                        Add Comment
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attachment View Modal */}
      {selectedAttachment && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50" onClick={closeAttachmentModal}>
          <div className="relative max-w-4xl w-full max-h-full" onClick={e => e.stopPropagation()}>
            <button
              onClick={closeAttachmentModal}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 z-10"
            >
              <X size={24} />
            </button>
            
            <div className="bg-white rounded-lg overflow-hidden">
              {attachmentLoading ? (
                <div className="p-12 text-center">
                  <Loader2 size={32} className="animate-spin mx-auto text-[#0b2045]" />
                  <p className="mt-2 text-gray-600">Loading attachment...</p>
                </div>
              ) : (
                <>
                  {selectedAttachment.contentType?.startsWith('image/') ? (
                    <img 
                      src={selectedAttachment.url} 
                      alt={selectedAttachment.filename}
                      className="max-w-full max-h-[70vh] object-contain mx-auto"
                    />
                  ) : selectedAttachment.contentType === 'application/pdf' ? (
                    <iframe 
                      src={selectedAttachment.url} 
                      className="w-full h-[70vh]"
                      title={selectedAttachment.filename}
                    />
                  ) : (
                    <div className="p-8 text-center">
                      {getFileIcon(selectedAttachment.contentType)}
                      <p className="text-gray-600 mt-4 mb-4">
                        {selectedAttachment.filename}
                      </p>
                      <button
                        onClick={() => {
                          const a = document.createElement('a');
                          a.href = selectedAttachment.url;
                          a.download = selectedAttachment.filename;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          toast.success('Download started');
                        }}
                        className="px-4 py-2 bg-[#0b2045] text-white rounded-lg hover:bg-[#152c53] inline-flex items-center transition-colors"
                      >
                        <Download size={16} className="mr-2" />
                        Download File
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Download button for images/PDFs */}
            {selectedAttachment && (selectedAttachment.contentType?.startsWith('image/') || 
              selectedAttachment.contentType === 'application/pdf') && (
              <button
                onClick={() => {
                  const a = document.createElement('a');
                  a.href = selectedAttachment.url;
                  a.download = selectedAttachment.filename;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  toast.success('Download started');
                }}
                className="absolute -bottom-12 right-0 bg-[#0b2045] text-white px-4 py-2 rounded-lg hover:bg-[#152c53] flex items-center transition-colors"
              >
                <Download size={16} className="mr-2" />
                Download
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AspirantReports;